import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AlertController, LoadingController } from '@ionic/angular';
import { LogErroDTO } from 'src/app/models/logErroDTO';
import { LogErroService } from '../log-erro/log-erro.service';

@Injectable({
    providedIn: 'root'
})
export class FunctionsService {

    isLoading = false;

    constructor(
        public loadingController: LoadingController,
        private alertController: AlertController,
        private router: Router,
        private logErroService: LogErroService
    ) { }

    async presentLoading(message: string = ''): Promise<void> {

        if (this.isLoading) {
            return;
        }

        this.isLoading = true;

        const response = await this.loadingController.create({
            // duration: 5000,
        });

        response.cssClass = 'custom-loading';
        response.spinner = 'crescent';
        response.message = message;

        await response.present();

        if (!this.isLoading) {
            await response.dismiss();
        }
    }

    async dismissLoading(): Promise<void> {
        try {
            this.isLoading = false;

            let erroDismiss = false;
            let cont = 0;

            while (!erroDismiss && cont < 3) {
                try {
                    await this.loadingController.dismiss();
                    cont++;
                } catch (_err) {
                    erroDismiss = true;
                }
            }
        } catch (_err) {
            return;
        }
    }

    async showAlert(title: string, message: string, buttons: Array<string | { text: string; handler?: () => void }>): Promise<void> {
        const alert = await this.alertController.create({
            header: title,
            message,
            buttons
        });

        await alert.present();
    }

    async showErrorAlert(
        title: string,
        message: string,
        error: unknown,
        buttons: Array<string | { text: string; handler?: () => void }>
    ): Promise<void> {

        console.error(error);

        const errorObj = error as {
            status?: number;
            error?: {
                status?: number;
                message?: string;
            };
            message?: string;
        };

        // IDENTIFICAÇÃO DE SESSÃO EXPIRADA
        // Mantém o comportamento existente para HTTP 403.
        const status = errorObj?.status ?? errorObj?.error?.status;

        if (status === 403) {

            await this.showAlert(
                'Atenção!',
                'Sua sessão expirou.<hr>Por favor, autentique-se novamente.',
                [{
                    text: 'Ok',
                    handler: () => {
                        this.navigate('modulo-gc/gc-login');
                    }
                }]
            );

            return;
        }

        // MENSAGEM RETORNADA PELO BACKEND
        const detalhe = errorObj?.error?.message || errorObj?.message;

        if (detalhe) {
            message += '<hr><b>Detalhes:</b><hr>' + detalhe;
        }

        await this.showAlert(title, message, buttons);
    }

    async showConfirmAlert(
        title: string,
        message: string,
        labelButtonConfirm: string,
        labelButtonCancel: string
    ): Promise<boolean> {

        return new Promise<boolean>(async (resolve) => {
            const alert = await this.alertController.create({
                header: title,
                message,
                buttons: [
                    {
                        text: labelButtonConfirm,
                        handler: () => {
                            alert.dismiss();
                            resolve(true);
                        }
                    },
                    {
                        text: labelButtonCancel,
                        handler: () => {
                            alert.dismiss();
                            resolve(false);
                        }
                    }
                ]
            });

            await alert.present();
        });
    }

    async showConfirmAlertGetValue(
        title: string,
        message: string,
        labelButtonConfirm: string,
        labelButtonCancel: string,
        inputPlaceHolder: string,
        inputValue: string,
        cssClassName?: string
    ): Promise<string | false> {

        return new Promise<string | false>(async (resolve) => {

            const alert = await this.alertController.create({
                header: title,
                message,
                cssClass: cssClassName,
                inputs: [
                    {
                        name: 'input',
                        type: 'text',
                        value: inputValue,
                        placeholder: inputPlaceHolder,
                    }
                ],
                buttons: [
                    {
                        text: labelButtonConfirm,
                        handler: (alertData: { input?: string }) => {
                            alert.dismiss();
                            resolve(alertData?.input ?? '');
                        }
                    },
                    {
                        text: labelButtonCancel,
                        handler: () => {
                            alert.dismiss();
                            resolve(false);
                        }
                    }
                ]
            });

            await alert.present();

            setTimeout(() => {
                const input = document.querySelector('ion-alert input') as HTMLInputElement | null;

                if (!input) {
                    return;
                }

                input.focus();

                input.addEventListener('keydown', (event: KeyboardEvent) => {
                    if (event.key === 'Enter') {
                        event.preventDefault();

                        const botaoConfirmar = document.querySelector('ion-alert button.alert-button') as HTMLButtonElement | null;

                        if (botaoConfirmar) {
                            botaoConfirmar.click();
                        }
                    }
                });
            }, 150);
        });
    }

    navigate(path: string): void {
        this.router.navigate([path]);
    }

    navigateWithParm(path: string, parm: unknown): void {
        this.router.navigate([path], { queryParams: { parm: JSON.stringify(parm) } });
    }

    goToPath(path: string, newWindow: boolean = false): void {

        if (!path) {
            return;
        }

        if (path.startsWith('/')) {
            path = path.replace(/\(/g, '%28').replace(/\)/g, '%29');
            path = this.normalizeUrl(path);
        }

        if (!newWindow) {
            window.location.href = path;
            return;
        }

        const newTab = window.open(path);

        if (!newTab || newTab.closed || typeof newTab.closed === 'undefined') {
            alert('Não foi possível abrir a nova janela. Verifique se o bloqueador de pop-ups está ativado no navegador.');
        }
    }

    normalizeUrl(url: string): string {

        if (
            url.toLocaleLowerCase().endsWith('.pdf') ||
            url.toLocaleLowerCase().endsWith('.doc') ||
            url.toLocaleLowerCase().endsWith('.png') ||
            url.toLocaleLowerCase().endsWith('.jpg') ||
            url.toLocaleLowerCase().endsWith('.jpeg') ||
            url.toLocaleLowerCase().includes('modulo-gc/')
        ) {
            return url;
        }

        url = decodeURIComponent(url);

        const charMap: Record<string, string> = {
            'á': 'a', 'é': 'e', 'í': 'i', 'ó': 'o', 'ú': 'u',
            'à': 'a', 'è': 'e', 'ì': 'i', 'ò': 'o', 'ù': 'u',
            'ã': 'a', 'õ': 'o', 'ñ': 'n',
            'â': 'a', 'ê': 'e', 'î': 'i', 'ô': 'o', 'û': 'u',
            'ç': 'c', 'ü': 'u',
            'Á': 'A', 'É': 'E', 'Í': 'I', 'Ó': 'O', 'Ú': 'U',
            'À': 'A', 'È': 'E', 'Ì': 'I', 'Ò': 'O', 'Ù': 'U',
            'Ã': 'A', 'Õ': 'O', 'Ñ': 'N',
            'Â': 'A', 'Ê': 'E', 'Î': 'I', 'Ô': 'O', 'Û': 'U',
            'Ç': 'C', 'Ü': 'U',
            '-': '_',
            ' ': '_',
            '(': '_',
            ')': '_',
        };

        const replaceChar = (char: string): string => charMap[char] || char;

        return url.replace(/[^A-Za-z0-9]/g, replaceChar);
    }

    sortArray<T extends { nome: string }>(arr: T[]): void {
        arr.sort((a: T, b: T) => {
            if (a.nome > b.nome) {
                return 1;
            }

            if (a.nome < b.nome) {
                return -1;
            }

            return 0;
        });
    }

    getDeepCopyArray<T>(source: T[]): T[] {
        return JSON.parse(JSON.stringify(source)) as T[];
    }

    getDateBrPattern(data: Date): string {

        data = new Date(data);

        const dia = data.getUTCDate().toString();
        const diaF = dia.length === 1 ? '0' + dia : dia;

        const mes = (data.getUTCMonth() + 1).toString();
        const mesF = mes.length === 1 ? '0' + mes : mes;

        const anoF = data.getUTCFullYear();

        return diaF + '/' + mesF + '/' + anoF;
    }

    getDateHourBrPattern(data: Date): string {

        data = new Date(data);

        const dia = data.getDate().toString();
        const diaF = dia.length === 1 ? '0' + dia : dia;

        const mes = (data.getMonth() + 1).toString();
        const mesF = mes.length === 1 ? '0' + mes : mes;

        const anoF = data.getFullYear();

        const hora = data.getHours().toString();
        const horaF = hora.length === 1 ? '0' + hora : hora;

        const min = data.getMinutes().toString();
        const minF = min.length === 1 ? '0' + min : min;

        return diaF + '/' + mesF + '/' + anoF + ' ' + horaF + ':' + minF;
    }

    getHourBrPattern(data: Date): string {

        const hora = data.getHours().toString();
        const horaF = hora.length === 1 ? '0' + hora : hora;

        const min = data.getMinutes().toString();
        const minF = min.length === 1 ? '0' + min : min;

        return horaF + ':' + minF;
    }

    getCurrencyValue(valor: unknown): string {
        const valorNumerico = valor ? Number.parseFloat(String(valor)) : 0;
        return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(valorNumerico);
    }

    getFormatedNumberValue(valor: unknown, qtdDigitosFracionados: number): string {
        return new Intl.NumberFormat(
            'pt-BR',
            {
                minimumFractionDigits: qtdDigitosFracionados,
                maximumFractionDigits: qtdDigitosFracionados
            }
        ).format(Number.parseFloat(String(valor)));
    }

    setCelPhoneMask(
        event: { detail: { inputType?: string }; target: { value: string } },
        mask: string
    ): void {

        if (mask === '(00) 00000-0000') {

            if (event.detail.inputType !== 'deleteContentBackward') {
                const x = event.target.value.replace(/\D/g, '').match(/(\d{0,2})(\d{0,5})(\d{0,4})/);

                if (!x) {
                    return;
                }

                event.target.value = '(' + x[1] + ') ' + x[2] + '-' + x[3];
            } else if (event.target.value.length < 15) {
                this.setPhoneMask(event, '(00) 0000-0000');
            }

        } else if (mask === '00000-0000') {

            if (event.detail.inputType !== 'deleteContentBackward') {
                const x = event.target.value.replace(/\D/g, '').match(/(\d{0,5})(\d{0,4})/);

                if (!x) {
                    return;
                }

                event.target.value = x[1] + '-' + x[2];
            } else if (event.target.value.length < 15) {
                this.setPhoneMask(event, '0000-0000');
            }
        }
    }

    setPhoneMask(
        event: { detail: { inputType?: string }; target: { value: string } },
        mask: string
    ): void {

        if (mask === '(00) 0000-0000') {

            if (event.detail.inputType !== 'deleteContentBackward' || event.target.value.length > 10) {
                const x = event.target.value.replace(/\D/g, '').match(/(\d{0,2})(\d{0,4})(\d{0,4})/);

                if (!x) {
                    return;
                }

                event.target.value = '(' + x[1] + ') ' + x[2] + '-' + x[3];
            }

        } else if (mask === '0000-0000') {

            if (event.detail.inputType !== 'deleteContentBackward' || event.target.value.length > 10) {
                const x = event.target.value.replace(/\D/g, '').match(/(\d{0,4})(\d{0,4})/);

                if (!x) {
                    return;
                }

                event.target.value = x[1] + '-' + x[2];
            }
        }
    }

    setCEPMask(event: { detail: { inputType?: string }; target: { value: string } }): void {

        if (event.detail.inputType !== 'deleteContentBackward') {
            const x = event.target.value.replace(/\D/g, '').match(/(\d{0,5})(\d{0,3})/);

            if (!x) {
                return;
            }

            event.target.value = x[1] + '-' + x[2];
        }
    }

    setCPFMask(event: { detail: { inputType?: string }; target: { value: string } }): void {

        if (event.detail.inputType !== 'deleteContentBackward') {
            const x = event.target.value.replace(/\D/g, '').match(/(\d{0,3})(\d{0,3})(\d{0,3})(\d{0,2})/);

            if (!x) {
                return;
            }

            if (x[1].length < 3) {
                event.target.value = x[1];
            } else if (x[1].length === 3 && x[2].length < 3) {
                event.target.value = x[1] + '.' + x[2];
            } else if (x[2].length === 3 && x[3].length < 3) {
                event.target.value = x[1] + '.' + x[2] + '.' + x[3];
            } else if (x[3].length === 3) {
                event.target.value = x[1] + '.' + x[2] + '.' + x[3] + '-' + x[4];
            }
        }
    }

    setDateMask(
        event: { detail: { inputType?: string }; target: { value: string } },
        mask: string
    ): void {

        if (mask === 'dd/MM/yyyy') {

            if (event.detail.inputType !== 'deleteContentBackward' || event.target.value.length > 10) {
                const x = event.target.value.replace(/\D/g, '').match(/(\d{0,2})(\d{0,2})(\d{0,4})/);

                if (!x) {
                    return;
                }

                event.target.value = x[1] + '/' + x[2] + '/' + x[3];

                if (x[1].length < 2) {
                    event.target.value = x[1];
                } else if (x[1].length === 2 && x[2].length < 2) {
                    event.target.value = x[1] + '/' + x[2];
                } else if (x[2].length === 2) {
                    event.target.value = x[1] + '/' + x[2] + '/' + x[3];
                }
            }

        } else if (mask === 'dd/MM/yyyy HH:mm:ss') {

            if (event.detail.inputType !== 'deleteContentBackward' || event.target.value.length > 10) {
                const x = event.target.value.replace(/\D/g, '').match(/(\d{0,2})(\d{0,4})(\d{0,4})(\d{0,2})(\d{0,2})(\d{0,2})/);

                if (!x) {
                    return;
                }

                event.target.value = x[1] + '/' + x[2] + '/' + x[3] + ' ' + x[4] + ':' + x[5] + ':' + x[6];
            }
        }
    }

    validateDate(date: string): boolean {

        if (date.length < 10) {
            this.showAlert(
                'Atenção!',
                'Data inválida!',
                [{
                    text: 'Ok',
                    handler: () => {
                        return;
                    }
                }]
            );

            return false;
        }

        const arrData = date.split('/');
        const dia = parseInt(arrData[0], 10);
        const mes = parseInt(arrData[1], 10);
        const ano = parseInt(arrData[2], 10);

        if (
            (dia > 31 || mes > 12 || ano > new Date().getFullYear()) ||
            (dia === 31 && [1, 3, 5, 7, 8, 10, 12].indexOf(mes) === -1)
        ) {
            this.showAlert(
                'Atenção!',
                'Data inválida!',
                [{
                    text: 'Ok',
                    handler: () => {
                        return;
                    }
                }]
            );

            return false;
        }

        return true;
    }

    validateCPF(cpf: string | null): boolean {

        if (!cpf || typeof cpf !== 'string') {
            return false;
        }

        cpf = cpf.replace(/[\s.-]*/igm, '');

        if (
            !cpf ||
            cpf.length !== 11 ||
            cpf === '00000000000' ||
            cpf === '11111111111' ||
            cpf === '22222222222' ||
            cpf === '33333333333' ||
            cpf === '44444444444' ||
            cpf === '55555555555' ||
            cpf === '66666666666' ||
            cpf === '77777777777' ||
            cpf === '88888888888' ||
            cpf === '99999999999'
        ) {
            return false;
        }

        let soma = 0;

        for (let i = 1; i <= 9; i++) {
            soma = soma + parseInt(cpf.substring(i - 1, i), 10) * (11 - i);
        }

        let resto = (soma * 10) % 11;

        if (resto === 10 || resto === 11) {
            resto = 0;
        }

        if (resto !== parseInt(cpf.substring(9, 10), 10)) {
            return false;
        }

        soma = 0;

        for (let i = 1; i <= 10; i++) {
            soma = soma + parseInt(cpf.substring(i - 1, i), 10) * (12 - i);
        }

        resto = (soma * 10) % 11;

        if (resto === 10 || resto === 11) {
            resto = 0;
        }

        if (resto !== parseInt(cpf.substring(10, 11), 10)) {
            return false;
        }

        return true;
    }

    addDays(date: Date, daysToAdd: number): Date {
        date.setDate(date.getDate() + daysToAdd);
        return date;
    }

    getRamdonString(bits: number): string {

        let result = '';
        const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
        const charactersLength = characters.length;

        for (let i = 0; i < bits; i++) {
            result += characters.charAt(Math.floor(Math.random() * charactersLength));
        }

        return result;
    }

    getMonthNameByIndex(index: number): string {

        const monthNames = [
            'Janeiro',
            'Fevereiro',
            'Março',
            'Abril',
            'Maio',
            'Junho',
            'Julho',
            'Agosto',
            'Setembro',
            'Outubro',
            'Novembro',
            'Dezembro'
        ];

        return monthNames[index];
    }

    getDayOfWeekName(date: Date): string {

        const dayNames = [
            'Domingo',
            'Segunda',
            'Terça',
            'Quarta',
            'Quinta',
            'Sexta',
            'Sábado'
        ];

        return dayNames[new Date(date).getDay()];
    }

    datesEqual(dateA: Date, dateB: Date): boolean {
        return new Date(dateA).getTime() === new Date(dateB).getTime();
    }

    insertLogErro(pagina: string, operacao: string, erro: unknown): void {

        const logErro = new LogErroDTO();
        logErro.data = new Date();
        logErro.operacao = operacao;
        logErro.pagina = pagina;
        logErro.erro = JSON.stringify(erro).substring(0, 15000);

        this.logErroService.insert(logErro).subscribe();
    }

    moveItemToStart<T extends { id: number }>(array: T[], idObjetc: number): T[] {
        const index = array.findIndex((obj: T) => obj.id === +idObjetc);

        if (index !== -1) {
            const objSelecionado = array.splice(index, 1)[0];
            array.unshift(objSelecionado);
        }

        return array;
    }
}